class SaCartDrawerRecommendations extends HTMLElement {
  constructor() {
    super();
    this._loading = false;
    this._onResize = this._onResize.bind(this);
  }

  connectedCallback() {
    if (this.dataset.disabled === 'true') return;
    window.addEventListener('resize', this._onResize);
    this.load();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this._onResize);
  }

  _onResize() {
    this.placeRecsHost();
  }

  isMobileViewport() {
    return window.matchMedia('(max-width: 999px)').matches;
  }

  getDrawerInner() {
    return this.closest('.drawer__inner');
  }

  getDesktopAnchor() {
    return this.getDrawerInner()?.querySelector('.sa-cart-drawer__panel');
  }

  getMobileSlot() {
    return document.getElementById('CartDrawer-MobileRecs');
  }

  setWithRecsLayout(active) {
    this.getDrawerInner()?.classList.toggle('sa-cart-drawer--with-recs', active);
  }

  placeRecsHost() {
    const drawerInner = this.getDrawerInner();
    const panel = this.getDesktopAnchor();
    const mobileSlot = this.getMobileSlot();
    const hasRecs = Boolean(this.querySelector('.sa-cart-drawer__recs'));

    if (!drawerInner || !panel) return;

    if (this.isMobileViewport() && mobileSlot && hasRecs) {
      if (this.parentElement !== mobileSlot) {
        mobileSlot.appendChild(this);
      }
      mobileSlot.classList.remove('hidden');
      mobileSlot.setAttribute('aria-hidden', 'false');
      this.setWithRecsLayout(false);
      return;
    }

    if (this.parentElement !== drawerInner) {
      drawerInner.insertBefore(this, panel);
    }

    if (mobileSlot) {
      mobileSlot.classList.add('hidden');
      mobileSlot.setAttribute('aria-hidden', 'true');
    }

    this.setWithRecsLayout(hasRecs);
  }

  getProductIds() {
    try {
      const parsed = JSON.parse(this.dataset.productIds || '[]');
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
      return [];
    }
  }

  parseSectionAside(htmlText) {
    const doc = new DOMParser().parseFromString(htmlText, 'text/html');
    return doc.querySelector('.sa-cart-drawer__recs');
  }

  extractCards(aside) {
    if (!aside) return [];
    return Array.from(aside.querySelectorAll('.sa-cart-drawer-recommend-card'));
  }

  filterCards(cards, excludeIds, collectedIds) {
    return cards.filter((card) => {
      const productId = card.dataset.productId;
      if (!productId) return false;
      if (excludeIds.has(productId)) return false;
      if (collectedIds.has(productId)) return false;
      return true;
    });
  }

  buildAside(cards) {
    const heading = this.dataset.heading || 'You May Also Like';
    const aside = document.createElement('aside');
    aside.className =
      'sa-cart-drawer__recs sa-cart-drawer__recs--aside hidden h-full min-h-0 w-full shrink-0 flex-col overflow-y-auto bg-[rgba(0,0,0,0.03)] md:flex md:w-[313px]';
    aside.setAttribute('aria-label', heading);
    aside.dataset.saRecsCount = String(cards.length);

    const inner = document.createElement('div');
    inner.className = 'sa-cart-drawer__recs-inner flex flex-col gap-12 px-12 md:gap-24 md:p-24';

    const title = document.createElement('h3');
    title.className =
      'sa-cart-drawer__recs-heading m-0 shrink-0 font-[Poppins] font-[500] text-[14px] leading-20 text-[rgba(0,0,0,0.95)] md:text-[20px] md:leading-28';
    title.textContent = heading;

    const list = document.createElement('div');
    list.className =
      'sa-cart-drawer__recs-list flex w-full max-w-full flex-row gap-8 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:w-[250px] md:flex-col md:gap-16 md:overflow-x-visible md:pb-0';
    cards.forEach((card) => list.appendChild(card));

    inner.append(title, list);
    aside.appendChild(inner);
    return aside;
  }

  mountAside(aside) {
    if (!aside) return false;
    this.replaceChildren(aside);
    this.placeRecsHost();
    return true;
  }

  clear() {
    this.replaceChildren();
    const mobileSlot = this.getMobileSlot();
    if (mobileSlot) {
      mobileSlot.classList.add('hidden');
      mobileSlot.setAttribute('aria-hidden', 'true');
    }

    const drawerInner = this.getDrawerInner();
    const panel = this.getDesktopAnchor();
    if (drawerInner && panel && this.parentElement !== drawerInner) {
      drawerInner.insertBefore(this, panel);
    }

    this.setWithRecsLayout(false);
  }

  async fetchRecommendationCards(productId, fetchLimit) {
    const limit = fetchLimit || this.dataset.limit || '4';
    const sectionId = this.dataset.sectionId || 'sa-cart-drawer-recommendations';
    const recommendationsUrl = this.dataset.recommendationsUrl;
    if (!recommendationsUrl) return [];

    const url = `${recommendationsUrl}?section_id=${sectionId}&product_id=${productId}&limit=${limit}&intent=related`;

    const response = await fetch(url);
    if (!response.ok) return [];

    const text = await response.text();
    return this.extractCards(this.parseSectionAside(text));
  }

  async fetchFallbackAside() {
    const sectionId = this.dataset.fallbackSectionId || 'sa-cart-drawer-recs-fallback';
    const cartUrl = this.dataset.cartUrl;
    if (!cartUrl) return null;

    const url = `${cartUrl}?section_id=${sectionId}`;

    const response = await fetch(url);
    if (!response.ok) return null;

    const text = await response.text();
    return this.parseSectionAside(text);
  }

  async load() {
    if (this.dataset.disabled === 'true') return;
    if (this._loading) return;
    this._loading = true;
    this.clear();

    const limit = parseInt(this.dataset.limit || '4', 10);
    const productIds = this.getProductIds();
    const excludeIds = new Set(productIds);
    const collectedIds = new Set();
    const collectedCards = [];
    const fetchLimit = limit + productIds.length;

    for (const productId of productIds) {
      if (collectedCards.length >= limit) break;

      const cards = await this.fetchRecommendationCards(productId, fetchLimit);
      const filtered = this.filterCards(cards, excludeIds, collectedIds).slice(0, limit - collectedCards.length);

      filtered.forEach((card) => {
        const id = card.dataset.productId;
        collectedIds.add(id);
        collectedCards.push(card);
      });
    }

    if (collectedCards.length > 0) {
      this.mountAside(this.buildAside(collectedCards));
      this._loading = false;
      return;
    }

    if (this.dataset.fallback === 'true') {
      const fallbackAside = await this.fetchFallbackAside();
      if (fallbackAside && this.mountAside(fallbackAside)) {
        this._loading = false;
        return;
      }
    }

    this._loading = false;
  }
}

if (!customElements.get('sa-cart-drawer-recommendations')) {
  customElements.define('sa-cart-drawer-recommendations', SaCartDrawerRecommendations);
}
